import { useState, useCallback } from 'react';
import { bookingService } from '../services/bookingService';

/**
 * Custom hook for booking operations
 */
export const useBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState({});
  const [bookingResult, setBookingResult] = useState(null);

  /**
   * Fetch meeting types for a client
   */
  const fetchMeetingTypes = useCallback(async (clientId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.getMeetingTypes(clientId);
      setMeetingTypes(result.meeting_types || {});
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Search for available time slots
   */
  const searchAvailability = useCallback(async (clientId, params) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.searchAvailability(clientId, params);
      
      // Extract slots from the calendars array based on selected calendar
      let slots = [];
      if (result.calendars && Array.isArray(result.calendars)) {
        // If calendar_name is specified in params, filter for that calendar
        if (params.calendar_name) {
          const calendar = result.calendars.find(cal => cal.calendar_name === params.calendar_name);
          if (calendar) {
            // Add calendar_name to each slot for proper booking
            slots = (calendar.available_slots || []).map(slot => ({
              ...slot,
              calendar_name: calendar.calendar_name,
              calendar_id: calendar.calendar_id
            }));
          }
        } else {
          // Otherwise, flatten all slots from all calendars and add calendar info
          slots = result.calendars.flatMap(cal => 
            (cal.available_slots || []).map(slot => ({
              ...slot,
              calendar_name: cal.calendar_name,
              calendar_id: cal.calendar_id
            }))
          );
        }
      }
      
      setAvailableSlots(slots);
      return { ...result, slots }; // Return slots for convenience
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Book an appointment
   */
  const bookAppointment = useCallback(async (bookingData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.bookAppointment(bookingData);
      setBookingResult(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reschedule a booking
   */
  const rescheduleBooking = useCallback(async (rebookData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.rescheduleBooking(rebookData);
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to reschedule booking';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel a booking
   */
  const cancelBooking = useCallback(async (customerId, eventId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.cancelBooking(customerId, eventId);
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to cancel booking';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get user's events/bookings
   */
  const getEvents = useCallback(async (customerId, filter = 'upcoming') => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.getEvents(customerId, filter);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear booking result
   */
  const clearBookingResult = useCallback(() => {
    setBookingResult(null);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    availableSlots,
    meetingTypes,
    bookingResult,
    fetchMeetingTypes,
    searchAvailability,
    bookAppointment,
    rescheduleBooking,
    cancelBooking,
    getEvents,
    clearBookingResult,
    clearError,
  };
};

export default useBooking;
